import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  CalendarIcon, 
  MapPinIcon, 
  UserGroupIcon, 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  PencilIcon,
  ArrowLeftIcon,
  PlusIcon,
  CalendarDaysIcon,
  UserIcon,
  TrashIcon,
  HandRaisedIcon,
  XMarkIcon as ReleaseIcon,
  LinkIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { eventsApi, Event as ApiEvent } from '../services/eventsApi'
import { useLocationsApi } from '../services/locationsApi'
import { useUsersApi } from '../services/usersApi'
import { isAdmin } from '../utils/roles'
import { formatDateLocal, formatDateRange, formatTime, getDaysArray } from '../utils/dateUtils'
import DailySchedule from '../components/DailySchedule'
import CreateScheduledEventModal from '../components/CreateScheduledEventModal'
import CreateFoodItemModal from '../components/CreateFoodItemModal'
import EditFoodItemModal from '../components/EditFoodItemModal'
import EditScheduledEventModal from '../components/EditScheduledEventModal'
import ScheduledEventDetailModal from '../components/ScheduledEventDetailModal'
import EditEventModal from '../components/EditEventModal'
import LoadingSpinner from '../components/LoadingSpinner'
import WhosComing from '../components/WhosComing'
import ChatPanel from '../components/ChatPanel'
import toast from 'react-hot-toast'

interface Location {
  id: string
  name: string
  address: string
  description?: string
}

interface Event {
  id: string
  title: string
  description: string
  startDate: string
  endDate?: string
  time: string
  locationId: string
  location?: Location
  maxAttendees?: number
  rsvpDeadline?: string
  createdBy: string
  createdAt: string
  isMultiDay: boolean
  rsvps: {
    userId: string
    userName?: string
    firstName?: string
    lastName?: string
    status: 'yes' | 'no' | 'maybe'
    timestamp: string
    days?: string[]
  }[]
  scheduledEvents: {
    [date: string]: {
      id: string
      time: string
      title: string
      description: string
      location?: string
      isOptional?: boolean
      maxParticipants?: number
      deadline?: string
      rsvps?: string[]
    }[]
  }
  dailyFoodSlots: {
    [date: string]: {
      id: string
      category: string
      description: string
      assignedTo?: string
      assignedUserName?: string
      dueDate?: string
    }[]
  }
}

type RSVPStatus = 'yes' | 'no' | 'maybe' | null

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const { user, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0()
  const locationsApi = useLocationsApi()
  const usersApi = useUsersApi()
  const [event, setEvent] = useState<ApiEvent | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [showScheduledEventModal, setShowScheduledEventModal] = useState(false)
  const [showFoodItemModal, setShowFoodItemModal] = useState(false)
  const [showEditFoodModal, setShowEditFoodModal] = useState(false)
  const [showEditScheduledEventModal, setShowEditScheduledEventModal] = useState(false)
  const [showScheduledEventDetailModal, setShowScheduledEventDetailModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [selectedFoodItem, setSelectedFoodItem] = useState<any>(null)
  const [selectedScheduledEvent, setSelectedScheduledEvent] = useState<any>(null)
  const [locations, setLocations] = useState<Location[]>([])
  const [updating, setUpdating] = useState(false)

  // Initialize API client when auth is ready
  useEffect(() => {
    if (!isAuthLoading) {
      eventsApi.setAuth(getAccessTokenSilently)
    }
  }, [isAuthLoading, getAccessTokenSilently])

  // Load locations for edit modal
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const data = await locationsApi.getLocations()
        const transformedLocations = data.locations.map((loc: any) => ({
          id: loc.locationId,
          name: loc.name,
          address: `${loc.address}${loc.city ? ', ' + loc.city : ''}${loc.state ? ', ' + loc.state : ''}`,
          description: loc.description
        }))
        setLocations(transformedLocations)
      } catch (error) {
      }
    }
    if (!isAuthLoading) {
      loadLocations()
    }
  }, [isAuthLoading, locationsApi])

  // Load event data from API
  useEffect(() => {
    if (!isAuthLoading && eventId) {
      loadEvent(eventId)
    }
  }, [isAuthLoading, eventId])

  const loadEvent = async (id: string) => {
    try {
      setLoading(true)
      const eventData = await eventsApi.getEvent(id)
      
      // Validate required fields
      if (!eventData || !eventData.eventId || !eventData.title || !eventData.eventDate) {
        throw new Error('Invalid event data received from API')
      }
      
      // Map eventDate to startDate if needed
      const event = {
        ...eventData,
        startDate: eventData.startDate || eventData.eventDate,
        rsvps: eventData.rsvps || [],
        scheduledEvents: eventData.scheduledEvents || {},
        dailyFoodSlots: eventData.dailyFoodSlots || {}
      }
      
      setEvent(event)
    } catch (error: any) {
      toast.error(error.message || 'Failed to load event')
    } finally {
      setLoading(false)
    }
  }

  const getUserRSVP = (): RSVPStatus => {
    if (!user || !event || !event.rsvps) return null
    const rsvp = event.rsvps.find(r => r.userId === user?.sub)
    return rsvp?.status || null
  }

  const handleRSVP = async (status: 'yes' | 'no' | 'maybe') => {
    if (!user || !event) return

    try {
      await eventsApi.rsvpToEvent(event.eventId, user.sub, status)
      
      // Fetch user profile to get correct joinedYear
      let userProfile
      try {
        userProfile = await usersApi.getUser(user.sub)
      } catch (error) {
        // Could not fetch user profile for RSVP
      }
      
      // Update local state optimistically
      if (!event.rsvps) event.rsvps = []
      const existingRsvpIndex = event.rsvps.findIndex(r => r.userId === user.sub)
      const newRsvp = {
        userId: user.sub,
        firstName: userProfile?.firstName || user.given_name || '',
        lastName: userProfile?.lastName || user.family_name || '',
        joinedYear: userProfile?.joinedYear || new Date().getFullYear().toString(),
        status,
        timestamp: new Date().toISOString()
      }

      const updatedRsvps = [...event.rsvps]
      if (existingRsvpIndex >= 0) {
        updatedRsvps[existingRsvpIndex] = newRsvp
      } else {
        updatedRsvps.push(newRsvp)
      }

      setEvent({ ...event, rsvps: updatedRsvps })
      toast.success('RSVP updated successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update RSVP')
    }
  }

  const handleFoodSlotClaim = async (slotId: string) => {
    if (!user || !event) return

    try {
      await eventsApi.claimFoodItem(event.eventId, slotId)
      
      // Get the proper user name from the API
      let userName = 'Unknown User'
      try {
        const userData = await usersApi.getUser(user.sub)
        userName = userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
      } catch (error) {
        // Fallback to Auth0 user data if API fails
        userName = user.name || `${user.given_name || ''} ${user.family_name || ''}`.trim()
      }
      
      // Update local state optimistically
      if (event.isMultiDay) {
        const updatedDailyFoodSlots = { ...event.dailyFoodSlots }
        Object.keys(updatedDailyFoodSlots).forEach(date => {
          updatedDailyFoodSlots[date] = updatedDailyFoodSlots[date].map(slot => {
            if (slot.id === slotId) {
              return {
                ...slot,
                assignedTo: user.sub,
                assignedUserName: userName
              }
            }
            return slot
          })
        })
        setEvent({ ...event, dailyFoodSlots: updatedDailyFoodSlots })
      } else {
        // Handle single-day events
        const foodSlots = event.dailyFoodSlots[event.startDate] || []
        const updatedFoodSlots = foodSlots.map(slot => {
          if (slot.id === slotId) {
            return {
              ...slot,
              assignedTo: user.sub,
              assignedUserName: userName
            }
          }
          return slot
        })
        setEvent({ 
          ...event, 
          dailyFoodSlots: {
            ...event.dailyFoodSlots,
            [event.startDate]: updatedFoodSlots
          }
        })
      }
      
      toast.success('Food item claimed successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to claim food item')
    }
  }

  const handleFoodSlotRelease = async (slotId: string) => {
    if (!user || !event) return

    try {
      await eventsApi.releaseFoodSlot(event.eventId, slotId)
      
      // Update local state optimistically
      if (event.isMultiDay) {
        const updatedDailyFoodSlots = { ...event.dailyFoodSlots }
        Object.keys(updatedDailyFoodSlots).forEach(date => {
          updatedDailyFoodSlots[date] = updatedDailyFoodSlots[date].map(slot => {
            if (slot.id === slotId && slot.assignedTo === user.sub) {
              return {
                ...slot,
                assignedTo: undefined,
                assignedUserName: undefined
              }
            }
            return slot
          })
        })
        setEvent({ ...event, dailyFoodSlots: updatedDailyFoodSlots })
      } else {
        // Handle single-day events
        const foodSlots = event.dailyFoodSlots[event.startDate] || []
        const updatedFoodSlots = foodSlots.map(slot => {
          if (slot.id === slotId && slot.assignedTo === user.id) {
            return {
              ...slot,
              assignedTo: undefined,
              assignedUserName: undefined
            }
          }
          return slot
        })
        setEvent({ 
          ...event, 
          dailyFoodSlots: {
            ...event.dailyFoodSlots,
            [event.startDate]: updatedFoodSlots
          }
        })
      }
      
      toast.success('Food item released successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to release food item')
    }
  }

  const getRSVPCounts = () => {
    if (!event || !event.rsvps) return { yes: 0, no: 0, maybe: 0 }
    const yes = event.rsvps.filter(r => r.status === 'yes').length
    const no = event.rsvps.filter(r => r.status === 'no').length
    const maybe = event.rsvps.filter(r => r.status === 'maybe').length
    return { yes, no, maybe }
  }

  const handleCreateScheduledEvent = async (date: string, eventData: any) => {
    if (!event) return

    try {
      const createdScheduledEvent = await eventsApi.addScheduledEvent(event.eventId, date, eventData)
      
      // Update local state with the real scheduled event from backend
      const updatedScheduledEvents = {
        ...event.scheduledEvents,
        [date]: [...(event.scheduledEvents[date] || []), createdScheduledEvent]
      }

      setEvent({ ...event, scheduledEvents: updatedScheduledEvents })
      toast.success('Activity added successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to add activity')
    }
  }

  const handleUpdateScheduledEvent = async (date: string, scheduledEventId: string, eventData: any) => {
    if (!event) return

    try {
      const updatedScheduledEvent = await eventsApi.updateScheduledEvent(event.eventId, scheduledEventId, eventData)
      
      // Update local state
      const updatedScheduledEvents = { ...event.scheduledEvents }
      const dateEvents = [...(updatedScheduledEvents[date] || [])]
      const eventIndex = dateEvents.findIndex(evt => evt.id === scheduledEventId)
      
      if (eventIndex !== -1) {
        dateEvents[eventIndex] = updatedScheduledEvent
        updatedScheduledEvents[date] = dateEvents
        setEvent({ ...event, scheduledEvents: updatedScheduledEvents })
        toast.success('Activity updated successfully!')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update activity')
    }
  }

  const handleDeleteScheduledEvent = async (date: string, scheduledEventId: string) => {
    if (!event) return

    try {
      await eventsApi.deleteScheduledEvent(event.eventId, scheduledEventId)
      
      // Update local state
      const updatedScheduledEvents = { ...event.scheduledEvents }
      const dateEvents = [...(updatedScheduledEvents[date] || [])]
      const eventIndex = dateEvents.findIndex(evt => evt.id === scheduledEventId)
      
      if (eventIndex !== -1) {
        dateEvents.splice(eventIndex, 1)
        if (dateEvents.length === 0) {
          delete updatedScheduledEvents[date]
        } else {
          updatedScheduledEvents[date] = dateEvents
        }
        setEvent({ ...event, scheduledEvents: updatedScheduledEvents })
        toast.success('Activity deleted successfully!')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete activity')
    }
  }

  const handleScheduledEventRsvp = async (date: string, scheduledEventId: string, attending: boolean) => {
    if (!event) return

    try {
      const updatedScheduledEvent = await eventsApi.rsvpScheduledEvent(event.eventId, scheduledEventId, attending)
      
      // Update local state
      const updatedScheduledEvents = { ...event.scheduledEvents }
      const dateEvents = [...(updatedScheduledEvents[date] || [])]
      const eventIndex = dateEvents.findIndex(evt => evt.id === scheduledEventId)
      
      if (eventIndex !== -1) {
        dateEvents[eventIndex] = updatedScheduledEvent
        updatedScheduledEvents[date] = dateEvents
        setEvent({ ...event, scheduledEvents: updatedScheduledEvents })
        toast.success(attending ? 'RSVP confirmed!' : 'RSVP removed!')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update RSVP')
    }
  }

  const handleCreateFoodItem = async (date: string, foodData: any) => {
    if (!event) return

    try {
      const createdFoodItem = await eventsApi.addFoodItem(event.eventId, date, foodData)
      
      // Update local state with the real food item from backend
      const newFoodItem = {
        id: createdFoodItem.foodId, // Use the real UUID from backend
        ...createdFoodItem
      }

      const updatedDailyFoodSlots = {
        ...event.dailyFoodSlots,
        [date]: [...(event.dailyFoodSlots[date] || []), newFoodItem]
      }

      setEvent({ ...event, dailyFoodSlots: updatedDailyFoodSlots })
      toast.success('Food item added successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to add food item')
    }
  }

  const handleUpdateEvent = async (eventData: any) => {
    if (!event) return

    try {
      setUpdating(true)
      await eventsApi.updateEvent(event.eventId, eventData)
      
      // Reload the event data
      await loadEvent(event.eventId)
      setShowEditModal(false)
      toast.success('Event updated successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update event')
    } finally {
      setUpdating(false)
    }
  }

  const handleUpdateFoodItem = async (foodId: string, updateData: any) => {
    if (!event) return

    try {
      await eventsApi.updateFoodItem(event.eventId, foodId, updateData)
      
      // Reload the event data to get updated food items
      await loadEvent(event.eventId)
      setShowEditFoodModal(false)
      setSelectedFoodItem(null)
      toast.success('Food item updated successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update food item')
    }
  }

  const handleDeleteFoodItem = async (foodId: string) => {
    if (!event) return

    if (!confirm('Are you sure you want to delete this food item?')) {
      return
    }

    try {
      await eventsApi.deleteFoodItem(event.eventId, foodId)
      
      // Reload the event data to get updated food items
      await loadEvent(event.eventId)
      toast.success('Food item deleted successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete food item')
    }
  }

  const canEdit = isAdmin(user) || user?.sub === event?.createdBy
  const isExpired = event && new Date(event.rsvpDeadline || event.startDate) < new Date()
  const userRsvp = getUserRSVP()
  const rsvpCounts = getRSVPCounts()

  if (isAuthLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Event not found</h3>
        <button
          onClick={() => navigate('/events')}
          className="mt-4 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          ← Back to Events
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/events')}
            className="flex items-center space-x-2 text-white hover:text-gray-200 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Events</span>
          </button>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/legacy/gallery?events=${encodeURIComponent(event.title)}`)}
            className="flex items-center space-x-2 px-4 py-2 border border-white/30 rounded-md text-sm font-medium text-white hover:bg-white/10 transition-colors"
          >
            <LinkIcon className="w-4 h-4" />
            <span>View Photos</span>
          </button>
          {canEdit && (
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center space-x-2 px-4 py-2 border border-white/30 rounded-md text-sm font-medium text-white hover:bg-white/10 transition-colors"
            >
              <PencilIcon className="w-4 h-4" />
              <span>Edit Event</span>
            </button>
          )}
        </div>
      </div>

      <div className="lg:hidden">
        <Link
          to={`/chat/event/${event.eventId}`}
          state={{
            title: `${event.title} chat`,
            subtitle: 'RSVPs (yes) only',
            locked: !(userRsvp === 'yes' || canEdit),
            lockedMessage: 'RSVP "Yes" to join this event chat.',
          }}
          className="inline-flex items-center px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/15"
        >
          Open Event Chat
        </Link>
      </div>
      <div className="lg:grid lg:grid-cols-3 lg:gap-6">
      <div className="lg:col-span-2 space-y-6">
      {/* Event Details Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="p-6">
          {/* Title and Description */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-theme-primary mb-4">
              {event.title}
            </h1>
            <p className="text-theme-secondary text-lg leading-relaxed">
              {event.description}
            </p>
          </div>

          {/* Event Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <div className="flex items-start space-x-3">
              <CalendarIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-1" />
              <div>
                <div className="font-medium text-theme-primary">Date & Time</div>
                <div className="text-theme-secondary">
                  {event.isMultiDay && event.endDate 
                    ? `${formatDateLocal(event.startDate)} - ${formatDateLocal(event.endDate)}`
                    : formatDateLocal(event.startDate)
                  }
                </div>
                <div className="text-theme-secondary">
                  {formatTime(event.time)}
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <MapPinIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-1" />
              <div>
                <div className="font-medium text-theme-primary">Location</div>
                <div className="text-theme-secondary">{event.location?.name}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{event.location?.address}</div>
                {event.location?.description && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {event.location.description}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <UserGroupIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-1" />
              <div>
                <div className="font-medium text-theme-primary">Attendance</div>
                <div className="text-theme-secondary">
                  {rsvpCounts.yes} attending
                  {event.maxAttendees && ` / ${event.maxAttendees} max`}
                </div>
                {event.rsvpDeadline && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    RSVP by {new Date(event.rsvpDeadline).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* Event URL */}
            {event.eventUrl && (
              <div className="flex items-start space-x-3">
                <LinkIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-1" />
                <div>
                  <div className="font-medium text-theme-primary">Event Website</div>
                  <a
                    href={event.eventUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline text-sm break-all"
                  >
                    Visit Event Website
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* RSVP Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Your RSVP</h3>
            
            {!isExpired ? (
              <div className="flex space-x-3 mb-4">
                <button
                  onClick={() => handleRSVP('yes')}
                  className={`px-6 py-3 rounded-md text-sm font-medium transition-colors ${
                    userRsvp === 'yes'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-green-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-green-900'
                  }`}
                >
                  <CheckCircleIcon className="w-4 h-4 inline mr-2" />
                  Yes, I'll attend ({rsvpCounts.yes})
                </button>
                <button
                  onClick={() => handleRSVP('maybe')}
                  className={`px-6 py-3 rounded-md text-sm font-medium transition-colors ${
                    userRsvp === 'maybe'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-yellow-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-yellow-900'
                  }`}
                >
                  <QuestionMarkCircleIcon className="w-4 h-4 inline mr-2" />
                  Maybe ({rsvpCounts.maybe})
                </button>
                <button
                  onClick={() => handleRSVP('no')}
                  className={`px-6 py-3 rounded-md text-sm font-medium transition-colors ${
                    userRsvp === 'no'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-red-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-red-900'
                  }`}
                >
                  <XCircleIcon className="w-4 h-4 inline mr-2" />
                  Can't attend ({rsvpCounts.no})
                </button>
              </div>
            ) : (
              <div className="text-gray-500 dark:text-gray-400 mb-4">
                RSVP deadline has passed
              </div>
            )}

            {/* Current RSVP Status */}
            {userRsvp && (
              <div className="flex items-center space-x-2 text-sm">
                {userRsvp === 'yes' && <CheckCircleIcon className="w-4 h-4 text-green-500" />}
                {userRsvp === 'no' && <XCircleIcon className="w-4 h-4 text-red-500" />}
                {userRsvp === 'maybe' && <QuestionMarkCircleIcon className="w-4 h-4 text-yellow-500" />}
                <span className="font-medium text-gray-900 dark:text-white">
                  You have RSVP'd: {userRsvp === 'yes' ? 'Attending' : userRsvp === 'no' ? 'Not Attending' : 'Maybe'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Combined Daily Schedule - Events and Food */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
            Daily Schedule & Food Coordination
          </h3>

          <div className="space-y-6">
            {getDaysArray(event.startDate, event.endDate).map((date) => {
              const scheduledEvents = event.scheduledEvents[date] || []
              const foodItems = event.dailyFoodSlots[date] || []
              
              return (
                <DailySchedule
                  key={date}
                  date={date}
                  events={scheduledEvents}
                  foodItems={foodItems}
                  currentUserId={user?.sub}
                  canEdit={canEdit}
                  isAdmin={isAdmin(user)}
                  isExpired={isExpired}
                  onAddEvent={(date) => {
                    setSelectedDate(date)
                    setShowScheduledEventModal(true)
                  }}
                  onAddFood={(date) => {
                    setSelectedDate(date)
                    setShowFoodItemModal(true)
                  }}
                  onClaimFood={handleFoodSlotClaim}
                  onReleaseFood={handleFoodSlotRelease}
                  onEditFood={(foodItem) => {
                    setSelectedFoodItem(foodItem)
                    setShowEditFoodModal(true)
                  }}
                  onDeleteFood={handleDeleteFoodItem}
                  onEditEvent={(date, scheduledEventId) => {
                    const scheduledEvent = scheduledEvents.find(evt => evt.id === scheduledEventId)
                    if (scheduledEvent) {
                      setSelectedScheduledEvent(scheduledEvent)
                      setSelectedDate(date)
                      setShowEditScheduledEventModal(true)
                    }
                  }}
                  onDeleteEvent={(date, scheduledEventId) => handleDeleteScheduledEvent(date, scheduledEventId)}
                  onRsvpEvent={(date, scheduledEventId, attending) => handleScheduledEventRsvp(date, scheduledEventId, attending)}
                  onEventClick={(date, scheduledEventId) => {
                    const scheduledEvent = scheduledEvents.find(evt => evt.id === scheduledEventId)
                    if (scheduledEvent) {
                      setSelectedScheduledEvent(scheduledEvent)
                      setSelectedDate(date)
                      setShowScheduledEventDetailModal(true)
                    }
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>

      {/* RSVP List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="p-6">
          <WhosComing rsvps={event.rsvps || []} />
        </div>
      </div>
      </div>

      <div className="hidden lg:block lg:col-span-1">
        <ChatPanel
          kind="event"
          entityId={event.eventId}
          title={`${event.title} chat`}
          subtitle="RSVPs (yes) only"
          placeholder="Message this event…"
          compact
          className="sticky top-24"
          locked={!(userRsvp === 'yes' || canEdit)}
          lockedMessage='RSVP "Yes" to join this event’s chat.'
          lockedActionLabel='RSVP Yes'
          onLockedAction={() => handleRSVP('yes')}
        />
      </div>
      </div>

      {/* Modals */}
      <CreateScheduledEventModal
        isOpen={showScheduledEventModal}
        date={selectedDate}
        onClose={() => {
          setShowScheduledEventModal(false)
          setSelectedDate('')
        }}
        onCreateScheduledEvent={handleCreateScheduledEvent}
      />

      <CreateFoodItemModal
        isOpen={showFoodItemModal}
        date={selectedDate}
        onClose={() => {
          setShowFoodItemModal(false)
          setSelectedDate('')
        }}
        onCreateFoodItem={handleCreateFoodItem}
      />

      <EditFoodItemModal
        isOpen={showEditFoodModal}
        foodItem={selectedFoodItem}
        onClose={() => {
          setShowEditFoodModal(false)
          setSelectedFoodItem(null)
        }}
        onUpdateFoodItem={handleUpdateFoodItem}
      />

      {event && (
        <EditEventModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          event={event}
          locations={locations}
          onSubmit={handleUpdateEvent}
          isLoading={updating}
        />
      )}

      <EditScheduledEventModal
        isOpen={showEditScheduledEventModal}
        onClose={() => {
          setShowEditScheduledEventModal(false)
          setSelectedScheduledEvent(null)
          setSelectedDate('')
        }}
        onSave={(eventData) => {
          if (selectedScheduledEvent && selectedDate) {
            handleUpdateScheduledEvent(selectedDate, selectedScheduledEvent.id, eventData)
          }
        }}
        scheduledEvent={selectedScheduledEvent}
        date={selectedDate}
      />

      <ScheduledEventDetailModal
        isOpen={showScheduledEventDetailModal}
        onClose={() => {
          setShowScheduledEventDetailModal(false)
          setSelectedScheduledEvent(null)
          setSelectedDate('')
        }}
        scheduledEvent={selectedScheduledEvent}
        date={selectedDate}
        currentUserId={user?.sub}
        onRsvp={(attending) => {
          if (selectedScheduledEvent && selectedDate) {
            handleScheduledEventRsvp(selectedDate, selectedScheduledEvent.id, attending)
          }
        }}
        canEdit={canEdit}
        isAdmin={isAdmin(user)}
        onEdit={() => {
          setShowScheduledEventDetailModal(false)
          setShowEditScheduledEventModal(true)
        }}
        onDelete={() => {
          if (selectedScheduledEvent && selectedDate) {
            handleDeleteScheduledEvent(selectedDate, selectedScheduledEvent.id)
          }
        }}
      />
      </motion.div>
    </div>
  )
}
